import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { useCallback, useEffect, useState } from "react";
import { setPreviousUrl } from "../../store/slices/geolocationSlice";

// Components
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import { Tabs } from "../../components/TabSwitch/Tabs";
import MainTable from "../../components/tables/MainTable";

// Utilities
import endpoints from "../../services/endpoints";
import { TableDetailsItem } from "../../components/tables/tableFunctions";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { adminContents } from "../AdminManagement/components/AdminManagementContent";
import ContentWrapper from "../../components/Add/ContentWrapper";

export default function OnboardingRequest({ tabType }: { tabType?: string }) {
  const { tab } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { onBoardingRequest } = endpoints;
  const { translate } = useTranslation(adminContents);
  const [fetchListTrigger, setFetchListTrigger] = useState(false);

  useEffect(() => {
    if (tab && tab !== 'onboarding' && tab !== 'modification') {
      navigate("/not-found");
    }
  }, [tab, navigate]);


  const handleTabChange = (newTab: string) => {
    navigate(`/onboarding-request/${newTab}`);
    setFetchListTrigger(prev => !prev);
  };

  const tabs = [
    {
      id: "onboarding",
      label: "Onboarding Request",
      content: <></>,
    },
    {
      id: "modification",
      label: "Modification Request",
      isDisabled: false,
      content: <></>,
    },
  ];

  // Table search placeholder states and animation
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>("user code");
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const SEARCH_PLACEHOLDERS = [
    "user code",
    "user name",
    "phone number"
  ] as const;

  const rotatePlaceholder = useCallback(() => {
    setIsTransitioning(true);

    setTimeout(() => {
      setSearchPlaceholder((prev) => {
        const currentIndex = SEARCH_PLACEHOLDERS.indexOf(
          prev as (typeof SEARCH_PLACEHOLDERS)[number]
        );
        const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length; // Loop cyclically
        return SEARCH_PLACEHOLDERS[nextIndex];
      });
      setIsTransitioning(false);
    }, 500);
  }, []);

  useEffect(() => {
    const interval = setInterval(rotatePlaceholder, 2500);
    return () => clearInterval(interval);
  }, [rotatePlaceholder]);

  // Table configuration
  const TableDetails: { [key: string]: TableDetailsItem } = {
    "User Code": {
      id: "user_code",
      className: "min-w-[120px]",
    },
    "User Name": {
      id: "name",
      sort: true,
      custom: ["first_name", "last_name"],
    },
    "User Location": {
      id: "user_location",
      className: "min-w-[214px]",
      custom: ["country", "province", "district"],
    },
    "User Role": {
      id: "user_role",
      className: "min-w-[150px]",
    },
    "Parent User Code and Name": {
      id: "onboarded_by_user_details",
      sort: true,
      custom: ["onboarded_by_user_details"],
    },
    "Requested Date": {
      id: "updated_at",
      sort: true,
      custom: ["updated_at"],
    },
    "Edited with Compliance": {
      id: "edited_with_compliance",
      custom: ["edited_with_compliance"],
    },
    "Phone Number": {
      id: "phone_number",
      custom: ["country_code", "phone_number"],
    },
    "Profile Status": {
      id: "profile",
      // className: "min-w-[100px]",
    },
    "Action Taken Date and Time": {
      id: "action_taken_at",
      custom: ["action_taken_at"],
    },
    Actions: {
      id: "actions",
      className: "min-w-[200px]",
      actionTypes: ["view"],
    },
  };

  const handleActions = async (id: string, key: string, text?: {
    id: string;
    user_code: string;
    first_name: string;
    last_name: string | null;
    country_code: string;
    phone_number: string;
    profile: string;
    country: string;
    province: string;
    district: string;
    updated_at: string;
    edited_with_compliance: string | null;
    action_taken_at: string | null;
    onboarded_by_user_details: {
      first_name: string;
      last_name: string;
      user_code: string;
    };
    user_role: string;
  }) => {
    dispatch(setPreviousUrl(location.pathname + location.search));    // navigate(`/raise-e-top-up-request/pay/${id}`);
    navigate(
      `/${tab}-request/${tab}/view-${tab}-request/${text?.user_role
        .toLowerCase()
        .replace(/ /g, "-")}/${id}`
    );
  };

  const staticText = {
    emptyScreenText: {
      heading1: translate(`There are no ${tabType || tab || "onboarding"} requests yet`),
      heading2: translate("Please check back later"),
    },
  };

  const dynamicFilter = {
    "User Role": {
      mapItem: [
        "Master Distributor",
        "Distributor",
        "Sub Distributor",
        "Agent",
        "Retailer",
        "Customer",
        "Temporary User",
      ],
      type: "checkbox",
    },
    "Profile Status": {
      mapItem: ["Approved", "Rejected", "In Review"],
      type: "checkbox",
    },
    "Requested Date Range": {
      type: "date",
      startDate: "requested_start_date",
      endDate: "requested_end_date",
    },
  };

  return (
    <div className="flex flex-col h-screen">
      <Breadcrumbs
        navPath={[
          "Onboarding / Modification Request",
          tabType === "onboarding" ||
            (!tabType && tab === "onboarding") ||
            (!tabType && tab === undefined)
            ? "List of Onboarding Request"
            : "List of Modification Request",
        ]}
        heading={"List of Onboarding Request"}
        noNavigation
      />

      <div className="pl-3 pr-[34px] pt-3">
        <ContentWrapper>
          <div className="pt-6 pl-6">
            <Tabs
              tabs={tabs}
              defaultTab="onboarding"
              activeTab={tabType ? tabType : tab}
              setActiveTab={handleTabChange}
            />
          </div>

          <MainTable
            staticText={staticText}
            mainEndPoint={`${tab === "onboarding" || tab === undefined
                ? onBoardingRequest
                : tab === "modification"
                ? `${onBoardingRequest}`
                : ""
            }`}
            TableDetails={TableDetails}
            handleActions={handleActions}
            params={{ page_number: "1" }}
            searchPlaceholder={`Search by ${searchPlaceholder}`}
            placeholderClassName={`search-placeholder ${isTransitioning ? "transitioning" : ""
              }`}
            dynamicFilter={dynamicFilter}
            TabChildren={true}
            filters={true}
            statusNotFilter={true}
            exporting={true}
            stickyStyleTableHeader={"top-[0.5px]"}
            modificationRequest={tab === "modification" ? true : false}
            // customHeight={true}
          />
        </ContentWrapper>
      </div>
    </div>
  );
}